'use client';
import React, { useState, useMemo } from 'react';
import ItemMasterTable from './ItemMasterTable';
import ItemMasterFilters from './ItemMasterFilters';
import AddItemModal from './AddItemModal';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

export type ItemCategory = 'RAW_MATERIAL' | 'CONSUMABLE' | 'SPARE_PART' | 'TOOL' | 'SAFETY_ITEM' | 'PPE' | 'FINISHED_GOOD' | 'OTHER';

export interface ItemMaster {
  id: string;
  itemCode: string;
  itemName: string;
  category: ItemCategory;
  uom: string;
  reorderLevel: number;
  safetyStock: number;
  unitCost: number;
  isQhseItem: boolean;
  hasExpiry: boolean;
  hsnCode: string;
  barcode: string;
  isActive: boolean;
  lastMovement: string;
}

export const MOCK_ITEMS: ItemMaster[] = [
  { id: 'item-001', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', category: 'CONSUMABLE', uom: 'L', reorderLevel: 50, safetyStock: 20, unitCost: 4.80, isQhseItem: false, hasExpiry: false, hsnCode: '27101960', barcode: '8901234500001', isActive: true, lastMovement: '25 Jul 2026' },
  { id: 'item-002', itemCode: 'SAF-HARN-01', itemName: 'Full Body Safety Harness', category: 'PPE', uom: 'EA', reorderLevel: 10, safetyStock: 5, unitCost: 142.00, isQhseItem: true, hasExpiry: true, hsnCode: '62113990', barcode: '8901234500002', isActive: true, lastMovement: '20 Jul 2026' },
  { id: 'item-003', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm² (per metre)', category: 'RAW_MATERIAL', uom: 'M', reorderLevel: 100, safetyStock: 50, unitCost: 1.35, isQhseItem: false, hasExpiry: false, hsnCode: '85444290', barcode: '8901234500003', isActive: true, lastMovement: '27 Jul 2026' },
  { id: 'item-004', itemCode: 'PPE-GLOVE-L', itemName: 'Nitrile Gloves Size Large (pair)', category: 'PPE', uom: 'PR', reorderLevel: 100, safetyStock: 50, unitCost: 0.95, isQhseItem: true, hasExpiry: true, hsnCode: '40151900', barcode: '8901234500004', isActive: true, lastMovement: '26 Jul 2026' },
  { id: 'item-005', itemCode: 'MECH-BRNG-6205', itemName: 'Deep Groove Ball Bearing 6205', category: 'SPARE_PART', uom: 'EA', reorderLevel: 20, safetyStock: 8, unitCost: 18.40, isQhseItem: false, hasExpiry: false, hsnCode: '84821020', barcode: '8901234500005', isActive: true, lastMovement: '18 Jul 2026' },
  { id: 'item-006', itemCode: 'CHEM-CEM-OPC', itemName: 'OPC Cement 53 Grade (50kg bag)', category: 'RAW_MATERIAL', uom: 'BAG', reorderLevel: 200, safetyStock: 80, unitCost: 7.20, isQhseItem: false, hasExpiry: true, hsnCode: '25232990', barcode: '8901234500006', isActive: true, lastMovement: '28 Jul 2026' },
  { id: 'item-007', itemCode: 'SAF-FEXT-CO2', itemName: 'Fire Extinguisher CO₂ 5kg', category: 'SAFETY_ITEM', uom: 'EA', reorderLevel: 8, safetyStock: 4, unitCost: 89.50, isQhseItem: true, hasExpiry: true, hsnCode: '84241000', barcode: '8901234500007', isActive: true, lastMovement: '15 Jul 2026' },
  { id: 'item-008', itemCode: 'TOOL-GRND-4IN', itemName: 'Angle Grinder 4" 850W', category: 'TOOL', uom: 'EA', reorderLevel: 5, safetyStock: 2, unitCost: 68.00, isQhseItem: false, hasExpiry: false, hsnCode: '84672110', barcode: '8901234500008', isActive: true, lastMovement: '12 Jul 2026' },
  { id: 'item-009', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm (per tonne)', category: 'RAW_MATERIAL', uom: 'MT', reorderLevel: 5, safetyStock: 2, unitCost: 680.00, isQhseItem: false, hasExpiry: false, hsnCode: '72142000', barcode: '8901234500009', isActive: true, lastMovement: '28 Jul 2026' },
  { id: 'item-010', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', category: 'PPE', uom: 'EA', reorderLevel: 20, safetyStock: 10, unitCost: 12.50, isQhseItem: true, hasExpiry: false, hsnCode: '65061090', barcode: '8901234500010', isActive: true, lastMovement: '22 Jul 2026' },
  { id: 'item-011', itemCode: 'SEAL-ORING-32', itemName: 'O-Ring NBR 32mm ID', category: 'SPARE_PART', uom: 'EA', reorderLevel: 50, safetyStock: 20, unitCost: 0.45, isQhseItem: false, hasExpiry: false, hsnCode: '40169300', barcode: '8901234500011', isActive: false, lastMovement: '01 Jun 2026' },
  { id: 'item-012', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308 (3.15mm)', category: 'CONSUMABLE', uom: 'KG', reorderLevel: 25, safetyStock: 10, unitCost: 14.20, isQhseItem: false, hasExpiry: false, hsnCode: '83110010', barcode: '8901234500012', isActive: true, lastMovement: '24 Jul 2026' },
];

export default function ItemMasterContent() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [visibleCols, setVisibleCols] = useState({
    hsnCode: true, barcode: false, lastMovement: true,
  });

  const filtered = useMemo(() => {
    return MOCK_ITEMS.filter((item) => {
      const matchSearch = search === '' ||
        item.itemCode.toLowerCase().includes(search.toLowerCase()) ||
        item.itemName.toLowerCase().includes(search.toLowerCase()) ||
        item.hsnCode.includes(search);
      const matchCat = categoryFilter === 'ALL' || item.category === categoryFilter;
      const matchStatus = statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && item.isActive) ||
        (statusFilter === 'INACTIVE' && !item.isActive);
      return matchSearch && matchCat && matchStatus;
    });
  }, [search, categoryFilter, statusFilter]);

  const handleBulkDeactivate = () => {
    toast.success(`${selectedIds.length} item(s) deactivated`);
    setSelectedIds([]);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flexitems-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-600 text-foreground">Item Master</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {filtered.length} of {MOCK_ITEMS.length} items · Last synced 28 Jul 2026 17:25
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.success('Export started — Excel file will download shortly.')}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors"
          >
            <Icon name="ArrowDownTrayIcon" size={15} />
            Export
          </button>
          <button
            onClick={() => setAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 shadow-card"
          >
            <Icon name="PlusIcon" size={16} />
            Add Item
          </button>
        </div>
      </div>

      {/* Filters */}
      <ItemMasterFilters
        search={search}
        onSearchChange={setSearch}
        categoryFilter={categoryFilter}
        onCategoryChange={setCategoryFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        visibleCols={visibleCols}
        onVisibleColsChange={setVisibleCols}
      />

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 bg-primary text-primary-foreground rounded-lg shadow-card slide-up">
          <span className="text-sm font-500">{selectedIds.length} item(s) selected</span>
          <div className="flex gap-2 ml-auto">
            <button
              onClick={handleBulkDeactivate}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-sm transition-colors"
            >
              <Icon name="NoSymbolIcon" size={14} />
              Deactivate
            </button>
            <button
              onClick={() => toast.success('Exporting selected items…')}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-sm transition-colors"
            >
              <Icon name="ArrowDownTrayIcon" size={14} />
              Export Selected
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="p-1.5 hover:bg-white/10 rounded-md transition-colors"
            >
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <ItemMasterTable
        items={filtered}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        visibleCols={visibleCols}
      />

      {/* Add Item Modal */}
      <AddItemModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />
    </div>
  );
}